import { PropsWithChildren, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useFetchComments } from 'domain/comment'
import useDirectory from 'hooks/useDirectory'
import { Building } from 'models/Building'
import MenuLeftDetailBuilding from 'pages/building/buildingDetail/components/MenuLeft/MenuLeftDetailBuilding'
import MenuRightDetailBuilding from 'pages/building/buildingDetail/components/MenuRight/rn/MenuRightDetailBuilding'
import PointVersionMenu from 'pages/building/floor/area/PointVersion/PointVersionMenu'
import Preview360 from 'pages/building/panoramas/components/Preview360'
import { useRecoilState, useRecoilValue } from 'recoil'
import {
  buildingModeState,
  selectedPoint,
  selectedPointDetail
} from 'store/buildingStore'
import { currentOpenMenu } from 'store/displayToggleStore'
import currentViewerState from 'store/viewerStore'
import useSWR from 'swr'
import { UserAction } from 'types/building'
import Utils from 'utils'

import Spinner from 'components/atoms/Spinner'
import ShowBarIcon from 'components/icons/ShowBarIcon'
import HeaderDetailBuilding from 'components/organisms/Header/HeaderDetailBuilding'

export type ActiveTab = '360' | 'document' | 'comment'

export default function DetailBuildingLayout({ children }: PropsWithChildren) {
  const { id } = useParams()
  const { navigate } = useDirectory()
  const { getBuiildingType } = useDirectory()

  const selectedPointValue = useRecoilValue(selectedPoint)
  const pointDetail = useRecoilValue(selectedPointDetail)
  const currentViewer = useRecoilValue(currentViewerState)

  const activePointId = useMemo(() => {
    return (
      (currentViewer?.selectedPoint
        ? String(currentViewer.selectedPoint)
        : undefined) ||
      (selectedPointValue ? String(selectedPointValue) : undefined) ||
      (pointDetail?.id ? String(pointDetail.id) : undefined) ||
      ''
    )
  }, [currentViewer?.selectedPoint, selectedPointValue, pointDetail?.id])

  const { unresolvedCount } = useFetchComments({
    buildingId: id!,
    pointId: activePointId
  })

  const buildingMode = useRecoilValue(buildingModeState)
  const [{ isOpenLeftMenu, isOpenRightMenu }, setIsOpenMenu] =
    useRecoilState(currentOpenMenu)

  const [activeTab, setActiveTab] = useState<ActiveTab>('360')

  useEffect(() => {
    if (!activePointId && activeTab === 'comment') {
      setActiveTab('360')
    }
  }, [activePointId, activeTab])

  const handleToggleLeftSidebar = () => {
    setIsOpenMenu((prev) => ({ ...prev, isOpenLeftMenu: !isOpenLeftMenu }))
  }

  const handleToggleRightSidebar = () => {
    setIsOpenMenu((prev) => ({ ...prev, isOpenRightMenu: !isOpenRightMenu }))
  }

  const handleTabClick = (tab: ActiveTab) => {
    if (isOpenRightMenu && activeTab === tab) {
      setIsOpenMenu((prev) => ({ ...prev, isOpenRightMenu: false }))
    } else {
      setActiveTab(tab)
      setIsOpenMenu((prev) => ({ ...prev, isOpenRightMenu: true }))
    }
  }

  const handleCollapse = () => {
    setIsOpenMenu((prev) => ({ ...prev, isOpenRightMenu: false }))
  }

  const handleMouseDown = () => {
    const historyToggler = document.getElementById('version-history-toggler')
    const pointActionsMenuContainer = document.getElementById(
      'point-action-menu-rightest-element'
    )
    const container = document.querySelector<HTMLElement>(
      '#detail-building-container'
    )
    const rightSidebar = document.querySelector<HTMLElement>(
      '#detail-building-right-sidebar'
    )
    const resize = (e: any) => {
      const containerSize = container?.offsetWidth || window.innerWidth
      const size = containerSize - e.clientX - 64
      if (rightSidebar) {
        if (size < 300) {
          rightSidebar.style.width = '300px'
        } else if (size > 530) {
          rightSidebar.style.width = '530px'
        } else {
          rightSidebar.style.width = `${size}px`
        }
      }

      if (historyToggler && pointActionsMenuContainer) {
        const togglerBound = historyToggler.getBoundingClientRect()
        const containerBound = pointActionsMenuContainer.getBoundingClientRect()
        if (containerBound.right + 5 >= togglerBound.left) {
          historyToggler.style.top = '98px'
          historyToggler.style.left = '-82px'
        }
        if (containerBound.right + 35 < togglerBound.left) {
          historyToggler.style.top = '20px'
          historyToggler.style.left = '-112px'
        }
      }
    }

    document.addEventListener('mousemove', resize, false)
    document.addEventListener(
      'mouseup',
      () => {
        document.removeEventListener('mousemove', resize, false)
      },
      false
    )
  }

  const { data: building, isValidating } = useSWR<Building>(
    {
      url: `/v1/buildings/by-type?id=${id}&building_type=${getBuiildingType()}`
    },
    {
      onError: (err) => {
        Utils.handleErrorNavigate(err, navigate)
      }
    }
  )

  useEffect(() => {
    if (building?.action === UserAction.VIEW) {
      navigate(`/viewer/buildings/${id}`)
    }
  }, [building?.action, id, navigate])

  return (
    <Spinner spinning={isValidating}>
      <div
        id="detail-building-container"
        className="h-screen grid grid-rows-[auto_1fr]"
      >
        <div className="h-[60px] relative">
          <HeaderDetailBuilding building={building} />
        </div>

        <div className="flex overflow-hidden">
          <div className="w-full h-full bg-[#F9FBFF] relative">
            {/* left sidebar menu */}
            <div className="absolute top-0 bottom-0 left-0 flex z-[750]">
              <div
                className="absolute cursor-pointer top-[10px] -right-10"
                onClick={handleToggleLeftSidebar}
              >
                <ShowBarIcon className={!isOpenLeftMenu ? 'rotate-180' : ''} />
              </div>
              <div
                className={`h-full theme-bg-third overflow-auto ${
                  isOpenLeftMenu ? 'min-w-[240px]' : 'w-0'
                }`}
              >
                <MenuLeftDetailBuilding building={building} />
              </div>
            </div>

            {/* middle content */}
            <div className="flex overflow-y-auto h-full">{children}</div>

            {/* right sidebar menu and navigator */}
            <div className="absolute top-0 bottom-0 right-0 flex z-[700] select-none flex-row h-full">
              {/* toggle right menu button */}
              <div
                className="absolute top-[10px] -left-10 cursor-pointer"
                onClick={handleToggleRightSidebar}
              >
                <ShowBarIcon className={!isOpenRightMenu ? '' : 'rotate-180'} />
              </div>

              {/* preview 360 window */}
              <Preview360 />

              {/* Slide-out Sidebar Panel */}
              {isOpenRightMenu && (
                <>
                  <div
                    className="w-[4px] cursor-ew-resize z-[600] relative h-full"
                    style={{
                      backgroundColor:
                        activeTab === 'comment'
                          ? 'rgb(255, 249, 230)'
                          : '#2b2312',
                      borderLeft: '1px solid #EAD9A6'
                    }}
                    onMouseDown={handleMouseDown}
                  >
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[2px] h-[19px] bg-[#CBB683] rounded-[20px]" />
                  </div>
                  <aside
                    id="detail-building-right-sidebar"
                    className={`panel-slide flex flex-none flex-col h-full ${
                      activeTab === 'comment'
                        ? 'bg-[#FFF9E6]'
                        : 'theme-bg-third'
                    }`}
                    style={{
                      width: '420px',
                      borderLeft: '1px solid #EAD9A6'
                    }}
                  >
                    <MenuRightDetailBuilding
                      activeTab={activeTab}
                      handleCollapse={handleCollapse}
                    />
                  </aside>
                </>
              )}

              {/* Right Navigator */}
              <div
                className="flex w-[60px] flex-none flex-col items-center border-l py-1.5 h-full"
                style={{
                  backgroundColor: 'rgb(212, 154, 25)',
                  borderColor: 'rgb(176, 129, 15)'
                }}
              >
                {/* 360 */}
                <button
                  type="button"
                  className={`relative mb-1 flex w-12 flex-col items-center gap-0.5 rounded-md py-1.5 transition border-0 bg-transparent outline-none focus:outline-none cursor-pointer ${
                    isOpenRightMenu && activeTab === '360'
                      ? 'text-white'
                      : 'hover:bg-black/10'
                  }`}
                  style={{
                    backgroundColor:
                      isOpenRightMenu && activeTab === '360'
                        ? 'rgb(100, 116, 139)'
                        : undefined,
                    color:
                      isOpenRightMenu && activeTab === '360'
                        ? undefined
                        : 'rgba(58, 46, 5, 0.72)'
                  }}
                  title="360"
                  onClick={() => handleTabClick('360')}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="lucide lucide-image"
                  >
                    <rect width="18" height="18" x="3" y="3" rx="2" ry="2" />
                    <circle cx="9" cy="9" r="2" />
                    <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" />
                  </svg>
                  <span className="text-[11px] font-bold leading-none whitespace-nowrap">
                    360
                  </span>
                </button>

                {/* 資料 */}
                <button
                  type="button"
                  className={`relative mb-1 flex w-12 flex-col items-center gap-0.5 rounded-md py-1.5 transition border-0 bg-transparent outline-none focus:outline-none cursor-pointer ${
                    isOpenRightMenu && activeTab === 'document'
                      ? 'text-white'
                      : 'hover:bg-black/10'
                  }`}
                  style={{
                    backgroundColor:
                      isOpenRightMenu && activeTab === 'document'
                        ? 'rgb(100, 116, 139)'
                        : undefined,
                    color:
                      isOpenRightMenu && activeTab === 'document'
                        ? undefined
                        : 'rgba(58, 46, 5, 0.72)'
                  }}
                  title="資料"
                  onClick={() => handleTabClick('document')}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="lucide lucide-folder-open"
                  >
                    <path d="m6 14 1.5-2.9A2 2 0 0 1 9.24 10H20a2 2 0 0 1 1.94 2.5l-1.54 6a2 2 0 0 1-1.95 1.5H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h3.9a2 2 0 0 1 1.69.9l.81 1.2a2 2 0 0 0 1.67.9H18a2 2 0 0 1 2 2v2" />
                  </svg>
                  <span className="text-[11px] font-bold leading-none whitespace-nowrap">
                    資料
                  </span>
                </button>

                {/* コメント */}
                {Boolean(activePointId) && (
                  <button
                    type="button"
                    className={`relative mb-1 flex w-12 flex-col items-center gap-0.5 rounded-md py-1.5 transition border-0 bg-transparent outline-none focus:outline-none cursor-pointer ${
                      isOpenRightMenu && activeTab === 'comment'
                        ? 'text-white'
                        : 'hover:bg-black/10'
                    }`}
                    style={{
                      backgroundColor:
                        isOpenRightMenu && activeTab === 'comment'
                          ? 'rgb(100, 116, 139)'
                          : undefined,
                      color:
                        isOpenRightMenu && activeTab === 'comment'
                          ? undefined
                          : 'rgba(58, 46, 5, 0.72)'
                    }}
                    title="コメント"
                    onClick={() => handleTabClick('comment')}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="lucide lucide-message-square-more"
                    >
                      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                      <path d="M8 10h.01" />
                      <path d="M12 10h.01" />
                      <path d="M16 10h.01" />
                    </svg>
                    <span className="text-[11px] font-bold leading-none whitespace-nowrap">
                      コメント
                    </span>
                    {unresolvedCount > 0 && (
                      <span
                        className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-rose-500 px-1 text-[9px] font-bold text-white"
                        style={{
                          boxShadow: 'rgb(212, 154, 25) 0px 0px 0px 2px'
                        }}
                      >
                        {unresolvedCount}
                      </span>
                    )}
                  </button>
                )}

                {/* パネルを畳む/開く */}

                <button
                  type="button"
                  className="mt-auto flex h-8 w-12 items-center justify-center rounded-md border-0 bg-transparent outline-none focus:outline-none cursor-pointer hover:bg-black/10 transition"
                  title={isOpenRightMenu ? 'パネルを畳む' : 'パネルを開く'}
                  style={{ color: 'rgba(58, 46, 5, 0.7)' }}
                  onClick={() =>
                    setIsOpenMenu((prev) => ({
                      ...prev,
                      isOpenRightMenu: !isOpenRightMenu
                    }))
                  }
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className={
                      isOpenRightMenu
                        ? 'lucide lucide-chevron-right'
                        : 'lucide lucide-chevron-left'
                    }
                  >
                    <path
                      d={isOpenRightMenu ? 'm9 18 6-6-6-6' : 'm15 18-6-6 6-6'}
                    />
                  </svg>
                </button>
              </div>
              {buildingMode.previewMode ? <PointVersionMenu /> : null}
            </div>
          </div>
        </div>
      </div>
    </Spinner>
  )
}
